<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useWindowSize } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';
import {
  branchWaInboxes,
  inboxPhoneDigits,
} from 'dashboard/helper/multiNumberHelper';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import { Virtualizer } from 'virtua/vue';
import ChatListHeader from './ChatListHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import PersonalChatTabs from './widgets/PersonalChatTabs.vue';
import GroupFolderBar from './widgets/GroupFolderBar.vue';
import GroupFolderModal from './widgets/GroupFolderModal.vue';
import MobileBranchChips from 'dashboard/components-next/branch-switcher/MobileBranchChips.vue';
import ConversationItem from './ConversationItem.vue';
import TasksView from './widgets/conversation/TasksView.vue';
import MyTasksTimeline from './widgets/conversation/MyTasksTimeline.vue';
import NewTaskModal from './widgets/conversation/NewTaskModal.vue';
import AgentsKanbanModal from './widgets/conversation/AgentsKanbanModal.vue';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useTrack } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import TasksAPI from 'dashboard/api/tasks';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import { conversationMatchesSearch } from '../helper/conversationSearchHelper.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { isPinnedPhone, isPinnedBranchCode } from 'dashboard/helper/pinnedContact';
import { branchColor } from 'dashboard/helper/branchColors';
import { conversationListPageURL, conversationUrl, frontendURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings, updateUISettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

// Barra del módulo Chat (Fase 2 · Victor 2026-06-11): en escritorio, el header y
// los filtros se teletransportan a la barra de ancho completo (#chatModuleTopbar)
// de ConversationView. En móvil se mantiene el layout en columna.
const { width: chatWindowWidth } = useWindowSize();
const isMobileChat = computed(() => chatWindowWidth.value < 768);

const resolveAttributesModalRef = ref(null);
const conversationListRef = ref(null);
const virtualListRef = ref(null);

provide('contextMenuElementTarget', virtualListRef);

// Default por rol (Victor 2026-05-28): super_admin entra a "Todos", el resto
// (admins, gerentes, vendedoras) entra directo a "Mi Chat" para que vean lo
// suyo primero y no se distraigan con la cola general.
const activeAssigneeTab = ref(
  (() => {
    const user = store.getters.getCurrentUser;
    const accountId = store.getters.getCurrentAccountId;
    const accountInfo = user?.accounts?.find(a => Number(a.id) === Number(accountId));
    return accountInfo?.super_admin === true
      ? wootConstants.ASSIGNEE_TYPE.ALL
      : wootConstants.ASSIGNEE_TYPE.ME;
  })()
);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
// Default (Victor 2026-05-17): el cliente con MÁS tiempo esperando respuesta
// arriba (waiting_since ascendente = espera más larga primero).
// Default "Conversación más reciente" para TODOS los agentes (Victor 2026-05-22):
// el cliente que escribe ahora aparece arriba. El agente puede cambiarlo con ↑↓.
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
// Filtro por canal (Victor 2026-05-20): cliente-side · mapea
// inbox.channel_type → categoría · default 'all'.
const activeChannel = ref('all');
const showAdvancedFilters = ref(false);

// Tab "Chats Urgentes" (I3 · M1.B · V2 2026-05-14): umbral en minutos sin
// respuesta del agente. Una conversación es "urgente" cuando el último mensaje
// real es del cliente (message_type=0 incoming) y han pasado >URGENT_THRESHOLD_MIN
// minutos sin que el agente responsable haya contestado.
// V2: el tab ahora muestra SOLO las convs asignadas al currentUser, agrupadas
// por sucursal · ya no clientes de otros agentes (que se vea solo lo MÍO).
const URGENT_THRESHOLD_MIN = 30;
// Disponibilidad para Urgentes (Victor 2026-06-07): toggle "Disponible/Ocupado"
// guardado en uiSettings (sincroniza cel↔PC). El reparto NO se toca; esto SOLO
// afecta mi propia vista de Urgentes. Ocupado → nada entra a Urgentes. Al volver
// a Disponible se reinicia el contador (gracia 30 min desde ese momento).
const urgentPaused = computed(() => uiSettings.value?.urgent_paused === true);
const urgentAvailableSince = computed(() => uiSettings.value?.urgent_available_since || 0);
function isUrgentConversation(c) {
  if (urgentPaused.value) return false; // Ocupado: ningún chat entra a Urgentes
  const lastMsg = c?.last_non_activity_message;
  if (!lastMsg) return false;
  if (lastMsg.message_type !== 0) return false; // 0 = incoming (mensaje del cliente)
  let tMs = lastMsg.created_at || 0;
  if (!tMs) return false;
  if (tMs < 1e12) tMs *= 1000; // chatwoot usa epoch en segundos · convertir a ms
  // Urgente si pasaron 30 min desde el mensaje O desde que volví a Disponible
  // (lo más reciente) → al reactivarme se reinicia el contador.
  const startMs = Math.max(tMs, urgentAvailableSince.value);
  return Date.now() - startMs > URGENT_THRESHOLD_MIN * 60 * 1000;
}
// Reclamable por mí (J3 · M1.B): la conversación me pertenece como responsable
// (assignee_id === currentUser.id) y el último mensaje saliente lo envió OTRO
// agente humano (no soy yo, no es un bot). Esto sucede cuando el responsable
// estuvo fuera, su lead entró a "Chats Urgentes" y otro agente respondió.
function isReclaimableByMe(c, currentUserId) {
  if (urgentPaused.value) return false; // Ocupado: tampoco muestro reclamables
  if (!currentUserId) return false;
  if (c?.meta?.assignee?.id !== currentUserId) return false;
  // Si ya fue reclamado → ya no es reclamable (evita doble click).
  if (c?.additional_attributes?.attending_reclaimed_at) return false;
  const lastMsg = c?.last_non_activity_message;
  if (!lastMsg) return false;
  if (lastMsg.message_type !== 1) return false; // 1 = outgoing (agente)
  if (lastMsg.sender_type !== 'User') return false; // descartar bots/sistema
  if (lastMsg.private) return false; // las private notes no cuentan como respuesta al cliente
  const senderId = lastMsg.sender?.id ?? lastMsg.sender_id;
  if (!senderId) return false;
  return senderId !== currentUserId;
}
// Tick reactivo cada 30s · fuerza re-cálculo del conteo y filtro sin reload del usuario.
const urgentTick = ref(0);

// Mis Tareas (Fase 1 · 2026-05-12) · lista de tareas asignadas, fetch al mount
// + cada vez que se entra al tab. Conteo refleja tareas pendientes/en proceso.
const tasksList = ref([]);
const tasksCount = ref(0);
const tasksLoading = ref(false);
const newTaskModalOpen = ref(false);
async function fetchTasks() {
  if (tasksLoading.value) return;
  tasksLoading.value = true;
  try {
    const { data } = await TasksAPI.list();
    tasksList.value = data?.data?.payload || [];
    tasksCount.value = tasksList.value.filter(t => t.status !== 'hecha').length;
  } catch (_) {
    // silent fail · mantenemos el snapshot anterior
  } finally {
    tasksLoading.value = false;
  }
}

// Q1 · 2026-05-12 · M1.B · Lista cross-branch para tab Chats Urgentes.
// Se fetchea del endpoint dedicado urgent_index que bypassa filtros de inbox/branch/perm.
// Almacenada local (no en Vuex) para no contaminar la lista regular de conversations.
// branches_meta viene del backend con TODAS las branches del account · permite
// etiquetar convs de sucursales a las que el agente no tiene acceso normal.
const urgentConversationsAll = ref([]);
const urgentBranchesMeta = ref({});
const urgentLoading = ref(false);
async function fetchUrgentConversationsAll() {
  if (urgentLoading.value) return;
  urgentLoading.value = true;
  try {
    const { data } = await ConversationAPI.urgentIndex();
    urgentConversationsAll.value = data?.data?.payload || [];
    urgentBranchesMeta.value = data?.data?.meta?.branches_meta || {};
  } catch (e) {
    // Silent fail · si falla mantenemos el último snapshot
  } finally {
    urgentLoading.value = false;
  }
}

// Tabs Selección / Retoque (Victor 2026-06-11): listas dedicadas que vienen del
// endpoint department_index (clientes con lead en Selección/Retoque de la
// sucursal activa). Almacenadas local (no en Vuex) igual que urgentes.
const seleccionConversations = ref([]);
const retoqueConversations = ref([]);
const impresionConversations = ref([]);
const albumConversations = ref([]);
const clientesConversations = ref([]);
const departmentLoading = ref(false);
async function fetchDepartmentConversations() {
  if (departmentLoading.value || !(hasSeleccionRole.value || hasImpresionRole.value || hasClientesRole.value)) return;
  departmentLoading.value = true;
  try {
    const { data } = await ConversationAPI.departmentIndex();
    seleccionConversations.value = data?.data?.seleccion || [];
    retoqueConversations.value = data?.data?.retoque || [];
    impresionConversations.value = data?.data?.impresion || [];
    albumConversations.value = data?.data?.album || [];
    clientesConversations.value = data?.data?.clientes || [];
  } catch (e) {
    // Silent fail · mantenemos el último snapshot
  } finally {
    departmentLoading.value = false;
  }
}
const searchQuery = ref('');
// Búsqueda DEDICADA · 2026-05-22 (Victor): la búsqueda usa el endpoint
// /conversations/search y guarda los resultados en `searchResults`, INDEPENDIENTE
// de allConversations. Antes la búsqueda metía sus resultados en la lista
// compartida y cualquier recarga de "abiertas" (websocket, scroll, watcher de
// filtros) los PISABA → el cliente aparecía o no de forma inconsistente. Ahora
// es estable: el cliente aparece esté en el estado/sucursal que esté.
const searchResults = ref([]);
const isSearching = ref(false);
let searchFetchTimer = null;
let searchSeq = 0;
watch(searchQuery, newVal => {
  clearTimeout(searchFetchTimer);
  const q = String(newVal || '').trim();
  if (q.length < 3) {
    // 1-2 chars: no consulta al servidor · el filtro local se aplica en la lista.
    searchResults.value = [];
    isSearching.value = false;
    return;
  }
  isSearching.value = true;
  const seq = ++searchSeq;
  searchFetchTimer = setTimeout(async () => {
    try {
      // Usamos el endpoint de LISTA con q (no /search): devuelve la conversación
      // COMPLETA (meta.sender con nombre/foto, inbox_id, status…) que la tarjeta
      // necesita para mostrar nombre y foto. status:'all' + assigneeType:'all'
      // para encontrar al cliente en cualquier estado y sucursal.
      const { data } = await ConversationAPI.get({
        q,
        status: 'all',
        assigneeType: 'all',
        page: 1,
      });
      if (seq !== searchSeq) return; // respuesta vieja · ignorar
      const payload = data?.data?.payload || [];
      // Set de contactos para que las tarjetas muestren nombre/foto.
      payload.forEach(c => {
        const sender = c?.meta?.sender;
        if (sender) store.dispatch('contacts/setContact', sender);
      });
      searchResults.value = payload;
    } catch (e) {
      if (seq === searchSeq) searchResults.value = [];
    } finally {
      if (seq === searchSeq) isSearching.value = false;
    }
  }, 350);
});
// FL1 · filtro por agentes asignados (multi-selección por sucursal)
const selectedAssigneeIds = ref([]);
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
// Mi Personal · filtros propios (Victor 2026-06-05): SOLO en la sucursal personal
// (is_personal) cambian las pestañas a Todos · Mis grupos · Tareas · Cerrados. Las
// 4 sucursales reales (SDO/SDE/Romana/Santiago) quedan igual.
const activeBranchRecord = useMapGetter('branches/getActiveBranch');
const isPersonalBranchActive = computed(
  () => !!activeBranchRecord.value?.is_personal
);
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

// Super admin del current user (para filtro "Leads Asignados")
const isCurrentUserSuperAdmin = computed(() => {
  const accountId = currentAccountId.value;
  const accountInfo = currentUser.value?.accounts?.find(a => Number(a.id) === Number(accountId));
  return accountInfo?.super_admin === true;
});

// Grupos de WhatsApp (Victor 2026-06-01): solo Víctor (uid 3) y Víctor Manuel
// (uid 7) ven los chats de grupo. Espejo de
// AgentPermissions::GROUPS_VISIBLE_USER_IDS en backend. El backend ya los oculta
// en el listado, la búsqueda y Urgentes; este filtro cubre los empujes en vivo
// por websocket que entran directo al store sin pasar por el finder.
const GROUPS_VISIBLE_USER_IDS = [3, 7];
const canSeeGroups = computed(() =>
  GROUPS_VISIBLE_USER_IDS.includes(currentUser.value?.id)
);
const isGroupConversation = c =>
  typeof c?.meta?.sender?.identifier === 'string' &&
  c.meta.sender.identifier.endsWith('@g.us');

// Grupos OFICIALES que SÍ aparecen en "Todos" de la personal (Victor 2026-06-05):
// el resto de grupos solo sale en "Mis grupos". Oficial Víctor Guzmán, La Romana,
// Equipo SDE y Santiago.
const OFFICIAL_GROUP_IDS = [
  '120363252339289744@g.us', // Oficial Víctor Guzmán Fotografía
  '120363405751497806@g.us', // Oficial La Romana Víctor Fotografía
  '120363404466875975@g.us', // Oficial Equipo SDE
  '120363399239307709@g.us', // Oficial Santiago Victor Fotografía
];
const isOfficialGroup = c => OFFICIAL_GROUP_IDS.includes(c?.meta?.sender?.identifier);

// Admin (encargado de sucursal) o super_admin del current user.
// Victor 2026-05-14 · ambos roles ven las tabs administrativas
// (Todos · Leads Entrantes · Leads Asignados · Leads Sin Asignar) ya filtradas
// automáticamente por la sucursal activa del switcher (Current.branch_id). El
// admin de una sola sucursal queda lockeado en ella · el admin con varias puede
// cambiar entre sus sucursales accesibles desde el switcher.
const canSeeAdminTabs = computed(() => {
  if (isCurrentUserSuperAdmin.value) return true;
  const accountId = currentAccountId.value;
  const accountInfo = currentUser.value?.accounts?.find(a => Number(a.id) === Number(accountId));
  return accountInfo?.role === 'administrator';
});

// Pestañas "Selección" / "Retoque" (Victor 2026-06-11): solo para la encargada
// del depto Selección/Retoque (flag feature_permissions.seleccion_retoque) y
// super_admin (Victor, para supervisar/probar). Filtran por la sucursal activa
// los clientes con lead en esos embudos que escriben.
// Supervisor de departamentos (Victor 2026-06-12): ve y responde los 5 filtros
// (Clientes/Selección/Retoque/Impresión/Álbum) de las 4 sucursales principales,
// con sub-chips de sucursal. Suma su visibilidad a los 3 grupos de pestañas.
const hasDeptSupervisorRole = computed(() => {
  if (isCurrentUserSuperAdmin.value) return true;
  const accountId = currentAccountId.value;
  const accountInfo = currentUser.value?.accounts?.find(a => Number(a.id) === Number(accountId));
  return accountInfo?.feature_permissions?.dept_supervisor === true;
});

const hasSeleccionRole = computed(() => {
  if (isCurrentUserSuperAdmin.value || hasDeptSupervisorRole.value) return true;
  const accountId = currentAccountId.value;
  const accountInfo = currentUser.value?.accounts?.find(a => Number(a.id) === Number(accountId));
  return accountInfo?.feature_permissions?.seleccion_retoque === true;
});

// Pestañas "Impresión" / "Álbum" (Victor 2026-06-11): mismo patrón, solo para el
// rol base de Impresión (flag impresion_album_scope) y super_admin. Cada rol ve
// solo sus 2 filtros — el de Selección ve Selección/Retoque, el de Impresión ve
// Impresión/Álbum.
const hasImpresionRole = computed(() => {
  if (isCurrentUserSuperAdmin.value || hasDeptSupervisorRole.value) return true;
  const accountId = currentAccountId.value;
  const accountInfo = currentUser.value?.accounts?.find(a => Number(a.id) === Number(accountId));
  return accountInfo?.feature_permissions?.impresion_album_scope === true;
});

// Filtro "Clientes" (Victor 2026-06-11): clientes del departamento Clientes que
// escriben. Visible para super_admin y el rol Cuentas por Cobrar (cuentas_cobrar_scope).
const hasClientesRole = computed(() => {
  if (isCurrentUserSuperAdmin.value || hasDeptSupervisorRole.value) return true;
  const accountId = currentAccountId.value;
  const accountInfo = currentUser.value?.accounts?.find(a => Number(a.id) === Number(accountId));
  return accountInfo?.feature_permissions?.cuentas_cobrar_scope === true;
});

// Sub-chips de sucursal para Impresión/Álbum (Victor 2026-06-11): la encargada
// (Mayerlyn) ve esos dos departamentos de las 4 sucursales y filtra por una con
// estos chips, con el color oficial de cada sucursal. null = todas.
const DEPT_BRANCH_CHIPS = [
  { id: 2, label: 'SDO', color: branchColor('sdo') },
  { id: 3, label: 'SDE', color: branchColor('sde') },
  { id: 5, label: 'Santiago', color: branchColor('stg') },
  { id: 9, label: 'La Romana', color: branchColor('rom') },
];
const activeDeptBranch = ref(null);
const showDeptBranchChips = computed(() => {
  const tab = activeAssigneeTab.value;
  // Impresión/Álbum: la encargada (impresion_album_scope) o el supervisor.
  if (['impresion', 'album'].includes(tab)) return hasImpresionRole.value;
  // Selección/Retoque/Clientes cross-sucursal: solo el supervisor de departamentos
  // (un encargado de una sola sucursal los ve sin chips, scopeados a la suya).
  if (['seleccion', 'retoque', 'clientes'].includes(tab)) return hasDeptSupervisorRole.value;
  return false;
});
function filterByDeptBranch(list) {
  if (!activeDeptBranch.value) return [...list];
  return list.filter(c => Number(c.branch_id) === Number(activeDeptBranch.value));
}

// Vista compartida SDO+SDE · 2026-05-19 (Victor): si las sucursales conectadas
// están activas en el account, la pestaña "Todos" se abre también a agentes
// regulares — el backend (PermissionFilterService) ya devuelve todas las
// conversaciones de las sucursales conectadas para cualquier rol bajo este
// scope. Sucursales NO conectadas (Personal Privado, etc.) se mantienen con el
// comportamiento original (solo admin/super ven Todos).
const sharedViewActive = computed(() => {
  const acc = store.getters['accounts/getAccount'](currentAccountId.value);
  const ids = acc?.settings?.connected_branch_ids;
  return Array.isArray(ids) && ids.length >= 2;
});

const assigneeTabItems = computed(() => {
  const baseTabs = filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  );
  const baseByKey = Object.fromEntries(baseTabs.map(t => [t.key, t]));
  const stats = conversationStats.value || {};

  // Counts client-side para tabs custom (basado en lista global de conversations)
  // Filtrar SOLO convs abiertas (Victor 2026-05-30): al marcar resuelta una conv,
  // sigue en allConversations con status='resolved' hasta que recargue. Si no
  // filtramos, los contadores de Mi chat/Todos/Urgentes no bajan al resolver.
  const allConvs = (store.getters['getAllConversations'] || []).filter(
    c => c.status === 'open'
  );
  const incomingCount = allConvs.filter(c => !c.first_reply_created_at || c.first_reply_created_at === 0).length;
  const assignedCount = allConvs.filter(c => c.meta?.assignee?.id).length;
  // Tocar urgentTick.value crea dependencia reactiva · el tick avanza cada 30s
  // para que el contador se mantenga actualizado aunque el usuario no interactúe.
  void urgentTick.value;
  const myId = currentUser.value?.id;
  // Q1 · 2026-05-12: usar lista cross-branch si está cargada · si está vacía
  // (primer load antes de fetchUrgentConversationsAll) fallback a allConvs.
  // urgentConversationsAll también puede traer resueltas tras un ciclo · filtrar
  // mismo criterio (status === 'open') que arriba para consistencia (2026-05-30).
  const sourceForUrgent = (urgentConversationsAll.value.length > 0
    ? urgentConversationsAll.value
    : allConvs).filter(c => c.status === 'open');
  // V2 · 2026-05-14 (Victor): SOLO mis convs asignadas · ya no chats de otros
  const urgentCount = sourceForUrgent.filter(
    c => c?.meta?.assignee?.id === myId
      && (isUrgentConversation(c) || isReclaimableByMe(c, myId))
      && !isReclaimedFromMe(c, myId)
      && !isMineAndReclaimed(c, myId)
  ).length;

  // Mi Personal (Victor 2026-06-05 · P2): solo 4 pestañas con íconos —
  // Todos · Mis grupos · Tareas · Cerrados. No aplica a SDO/SDE/Romana/Santiago.
  if (isPersonalBranchActive.value) {
    const groupsCount = allConvs.filter(isGroupConversation).length;
    return [
      { key: 'all', name: '💬 Todos', count: stats[baseByKey.all?.count] || 0 },
      { key: 'mygroups', name: '👥 Grupos', count: groupsCount },
      { key: 'tasks', name: '✓ Tareas', count: tasksCount.value },
      { key: 'closed', name: '✔️ Cerrados', count: stats.resolvedCount || 0, variant: 'success' },
    ];
  }

  const tabs = [];
  // Tabs administrativos · super admin Y administrator (Victor 2026-05-14):
  // El encargado de una sucursal (role administrator) tambien ve estos tabs,
  // pero el contenido viene YA filtrado por la sucursal activa del switcher
  // (backend: for_active_branch). Los agents normales siguen viendo solo Mi
  // Chat / Urgentes / Cerrados.
  // Pestaña "Todos" · 2026-05-20 (Victor): visible para TODOS los roles
  // (admin/super/agent). El backend filtra automáticamente por la sucursal
  // activa del switcher (Current.branch_id) — cada agente ve solo los chats
  // de SU sucursal. Las pestañas de Leads (Entrantes/Asignados/Sin Asignar)
  // siguen siendo solo admin/super.
  if (baseByKey.all) {
    tabs.push({ key: 'all', name: 'Todos', count: stats[baseByKey.all.count] || 0 });
  }
  // Pestañas Selección / Retoque (Victor 2026-06-11): al lado de Todos, solo para
  // quien tenga el rol. Punto de color (azul/naranja) + conteo de la sucursal.
  if (hasSeleccionRole.value) {
    tabs.push({ key: 'seleccion', name: 'Selección', count: seleccionConversations.value.length, dot: '#4f8cff' });
    tabs.push({ key: 'retoque', name: 'Retoque', count: retoqueConversations.value.length, dot: '#d97a3d' });
  }
  if (hasImpresionRole.value) {
    tabs.push({ key: 'impresion', name: 'Impresión', count: impresionConversations.value.length, dot: '#a78bfa' });
    tabs.push({ key: 'album', name: 'Álbum', count: albumConversations.value.length, dot: '#34d399' });
  }
  if (hasClientesRole.value) {
    tabs.push({ key: 'clientes', name: 'Clientes', count: clientesConversations.value.length, dot: '#e08fb8' });
  }
  if (canSeeAdminTabs.value) {
    tabs.push({ key: 'incoming_leads', name: 'Leads Entrantes', count: incomingCount });
    tabs.push({ key: 'assigned_leads', name: 'Leads Asignados', count: assignedCount });
    if (baseByKey.unassigned) {
      tabs.push({ key: 'unassigned', name: 'Leads Sin Asignar', count: stats[baseByKey.unassigned.count] || 0 });
    }
  }
  if (baseByKey.me) {
    // Q1 v4 · 2026-05-12: count client-side para que coincida con la lista
    // filtrada de Mi Chat (excluye urgentes no reclamadas).
    const meCount = sourceForUrgent.filter(c =>
      c?.meta?.assignee?.id === myId
      && c?.status === 'open'
      && !isExcludedFromMine(c, myId)
    ).length;
    tabs.push({ key: 'me', name: 'Mi Chat', count: meCount });
  }
  // Tab "Chats Urgentes" (variant danger · I3 · M1.B): conversaciones con último
  // mensaje del cliente sin responder por más de URGENT_THRESHOLD_MIN minutos.
  // Es global · ignora la pestaña actual y el agente responsable. Click parpadeo.
  tabs.push({ key: 'urgent', name: '⚠ Chats Urgentes', count: urgentCount, variant: 'danger' });
  // Tab "Mis Tareas" (Fase 1 · 2026-05-12): tareas asignadas a agentes con
  // posibilidad de vinculación a cliente. Super admin ve todas las del account;
  // agentes ven solo las propias. Conteo se carga dinámicamente desde la vista.
  tabs.push({ key: 'tasks', name: '📋 Mis Tareas', count: tasksCount.value });
  // Tab "Cerrados" (variant success · L1 · M1.B): status=resolved · al entrar
  // cambia activeStatus a 'resolved' y carga las cerradas. Count REAL desde el
  // backend (resolved_count · mismo scope sucursal/permisos · Victor 2026-06-03);
  // antes se calculaba sobre la memoria filtrada a abiertas y siempre daba 0.
  const closedCount = stats.resolvedCount || 0;
  tabs.push({ key: 'closed', name: '✓ Cerrados', count: closedCount, variant: 'success' });
  return tabs;
});

// Si el activeAssigneeTab actual ya no existe en los tabs visibles (caso típico:
// agente no-super-admin con default 'all' tras esconder ese tab), saltar a 'me'
// que siempre está visible. Evita estado roto al cargar la app.
watch(
  () => assigneeTabItems.value.map(t => t.key).join('|'),
  () => {
    const keys = assigneeTabItems.value.map(t => t.key);
    if (keys.length === 0) return;
    if (!keys.includes(activeAssigneeTab.value)) {
      activeAssigneeTab.value = keys.includes('me') ? 'me' : keys[0];
    }
  },
  { immediate: true }
);

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  // Guard: al cambiar de sucursal el set de tabs cambia y, por un instante, el
  // tab activo puede no existir en el nuevo set (el watcher lo corrige enseguida).
  const item = assigneeTabItems.value.find(
    t => t.key === activeAssigneeTab.value
  );
  return item?.count ?? 0;
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

// Tabs custom (incoming_leads, assigned_leads) son client-side; al backend van como 'all'.
// 'tasks' (Fase 1 · 2026-05-12) no usa el endpoint de conversaciones · pero le mapeo a 'all'
// para evitar requests inválidos por si el watcher de conversationFilters dispara fetch.
const effectiveAssigneeType = computed(() => {
  const tab = activeAssigneeTab.value;
  // 'mygroups' (Mi Personal · Victor 2026-06-05): al backend va como 'all' y los
  // grupos se filtran client-side en conversationList.
  if (
    tab === 'incoming_leads' ||
    tab === 'assigned_leads' ||
    tab === 'tasks' ||
    tab === 'mygroups' ||
    tab === 'seleccion' ||
    tab === 'retoque' ||
    tab === 'impresion' ||
    tab === 'album' ||
    tab === 'clientes'
  ) {
    return wootConstants.ASSIGNEE_TYPE.ALL;
  }
  return tab;
});

// Filtro por canal SERVER-SIDE (Victor 2026-06-06): se manda `channel` en
// conversationFilters y el FINDER lo resuelve contra todas las bandejas de la
// cuenta, aplicado sobre lo que el usuario ya puede ver (branch/permiso). Antes
// se resolvía client-side con inboxesList (membresía), pero los agentes ven
// chats por sucursal, no por membresía → WhatsApp daba lista vacía.

const conversationFilters = computed(() => {
  return {
    // inboxId solo cuando se entra a una bandeja específica (no para el filtro de
    // canal). El filtro de canal viaja como `channel` y lo resuelve el SERVIDOR
    // contra todas las bandejas — robusto frente a la membresía (Victor 2026-06-06).
    inboxId: props.conversationInbox || undefined,
    channel:
      activeChannel.value && activeChannel.value !== 'all'
        ? activeChannel.value
        : undefined,
    assigneeType: effectiveAssigneeType.value,
    // Pestaña "Grupos" (Victor 2026-06-06): pide al SERVIDOR TODOS los grupos de
    // la cuenta (sin importar sucursal), para que aparezcan todos y consistentes
    // en sus carpetas. Antes solo se veían los grupos de la página ya cargada.
    onlyGroups: activeAssigneeTab.value === 'mygroups' ? true : undefined,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    // Vista por sucursal (Victor 2026-06-11): el PRIMER lote es chico (40) para que
    // las primeras tarjetas + cabeceras (con conteos reales del servidor) aparezcan
    // al instante; los lotes siguientes son grandes (200) y entran en segundo plano
    // solo si hace falta (al abrir una sucursal). En otras vistas el backend usa su
    // default (75).
    perPage: branchGroupingActive.value ? (currentPage.value < 1 ? 40 : 200) : undefined,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === 'mention') {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (props.conversationType === 'participating') {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === 'unattended') {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

const conversationList = computed(() => {
  // Búsqueda global (Victor 2026-05-22): si hay texto en el buscador, el cliente
  // debe aparecer SIN IMPORTAR pestaña ni estado (abiertas, resueltas, urgentes,
  // mías, todas). El backend ya trajo coincidencias de todos los estados (watcher
  // de searchQuery con status:'all'); aquí buscamos sobre todas las convs
  // accesibles forzando status:'all' y respetando permisos por rol.
  const sq = searchQuery.value.trim();
  if (sq) {
    if (sq.length >= 3) {
      // Resultados del endpoint dedicado (todos los estados/sucursales),
      // independientes de la lista que otros fetches sobrescriben.
      // 2026-05-30 (Victor): aplicar collapseByContact igual que la lista
      // normal, para no mostrar 1 tarjeta por número cuando el cliente escribió
      // a varios números de la misma sucursal (ej. Personal con 2 números).
      return collapseByContact(
        [...searchResults.value].sort(
          (a, b) => getLastMsgTs(b) - getLastMsgTs(a)
        )
      );
    }
    // 1-2 caracteres: filtro local rápido sobre lo ya cargado.
    return collapseByContact(
      allChatList
        .value({ ...conversationFilters.value, status: 'all' })
        .filter(c => conversationMatchesSearch(c, sq))
        .filter(c => canSeeGroups.value || !isGroupConversation(c))
        .sort((a, b) => getLastMsgTs(b) - getLastMsgTs(a))
    );
  }

  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    const tab = activeAssigneeTab.value;
    if (tab === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
      // Q1 v4 · 2026-05-12: convs urgentes salen de Mi Chat · viven en Urgentes
      // hasta que se reclamen o se responda al cliente. Reclamadas vuelven aquí.
      void urgentTick.value;
      const myId = currentUser.value?.id;
      localConversationList = localConversationList.filter(
        c => !isExcludedFromMine(c, myId)
      );
    } else if (tab === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else if (tab === 'seleccion') {
      // Selección (Victor 2026-06-11): lista dedicada del backend (clientes con
      // lead en embudo Selección). Cross-sucursal + sub-chips para el supervisor.
      localConversationList = filterByDeptBranch(seleccionConversations.value);
    } else if (tab === 'retoque') {
      localConversationList = filterByDeptBranch(retoqueConversations.value);
    } else if (tab === 'impresion') {
      localConversationList = filterByDeptBranch(impresionConversations.value);
    } else if (tab === 'album') {
      localConversationList = filterByDeptBranch(albumConversations.value);
    } else if (tab === 'clientes') {
      localConversationList = filterByDeptBranch(clientesConversations.value);
    } else {
      // 'all', 'incoming_leads', 'assigned_leads' → todas
      localConversationList = [...allChatList.value(filters)];
    }
    // Sub-filtros client-side para tabs custom
    if (tab === 'mygroups') {
      // Mi Personal · "Mis grupos" (Victor 2026-06-05): solo conversaciones de
      // grupos de WhatsApp (identifier @g.us).
      localConversationList = localConversationList.filter(isGroupConversation);
      // Carpeta de grupos activa (chips P2): filtra a los grupos asignados a esa
      // carpeta. null = "Todos" (sin filtro extra).
      if (activeGroupFolder.value) {
        localConversationList = localConversationList.filter(c =>
          groupMatchesFolder(
            c.meta?.sender?.identifier,
            c.meta?.sender?.name,
            activeGroupFolder.value
          )
        );
      }
    } else if (tab === 'incoming_leads') {
      localConversationList = localConversationList.filter(c =>
        !c.first_reply_created_at || c.first_reply_created_at === 0
      );
    } else if (tab === 'assigned_leads') {
      localConversationList = localConversationList.filter(c =>
        c.meta?.assignee?.id
      );
    } else if (tab === 'urgent') {
      // Chats Urgentes · 2026-05-27 (Victor): se renderiza como tarjeta normal
      // (igual que Todos/Mi Chat). La fuente es urgentConversationsAll (fetch
      // dedicado cross-branch, sin filtro de inbox/branch/perm) con fallback al
      // store regular. Umbral 30 min sin respuesta · el tick recalcula cada 30s.
      void urgentTick.value;
      const myId = currentUser.value?.id;
      const urgentSource = urgentConversationsAll.value.length > 0
        ? urgentConversationsAll.value
        : localConversationList;
      localConversationList = urgentSource.filter(
        c => c?.meta?.assignee?.id === myId
          && (isUrgentConversation(c) || isReclaimableByMe(c, myId))
          && !isReclaimedFromMe(c, myId)
          && !isMineAndReclaimed(c, myId)
      );
    } else if (tab === 'closed' && !isCurrentUserSuperAdmin.value) {
      // No-super-admin (M · 2026-05-12): solo ve sus propias cerradas, no las
      // de otros agentes. Super admin sigue viendo todas las status=resolved.
      const myId = currentUser.value?.id;
      localConversationList = localConversationList.filter(
        c => c.meta?.assignee?.id === myId
      );
    }
    // Mi Personal · "Todos" sin grupos salvo los 4 oficiales (Victor 2026-06-05):
    // en la pestaña Todos de la personal, los grupos NO se mezclan con los chats
    // normales; solo los oficiales aparecen. El resto vive en "Mis grupos".
    if (isPersonalBranchActive.value && tab === 'all') {
      localConversationList = localConversationList.filter(
        c => !isGroupConversation(c) || isOfficialGroup(c)
      );
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }


  // FL1 · filtro por agentes asignados
  if (selectedAssigneeIds.value.length > 0) {
    const ids = selectedAssigneeIds.value;
    localConversationList = localConversationList.filter(conversation => {
      const assigneeId = conversation.meta?.assignee?.id;
      return assigneeId && ids.includes(assigneeId);
    });
  }

  // Filtro por canal: ya NO es client-side. El servidor pagina solo los inboxes
  // del canal (ver channelInboxIds + conversationFilters). Aquí no se filtra nada.

  // Grupos ocultos (Victor 2026-06-01): si el usuario no está en la whitelist,
  // descarta cualquier chat de grupo que haya entrado por empuje en vivo.
  // Grupos SOLO en la sucursal Personal (Victor 2026-06-05): en las 4 sucursales
  // de negocio (SDO/SDE/La Romana/Santiago) los grupos NO se muestran a NADIE —
  // ni a la whitelist. Viven únicamente en la Personal (con sus carpetas). Aquí
  // se descartan los empujes en vivo; el backend ya los oculta a no-whitelist.
  if (!canSeeGroups.value || !isPersonalBranchActive.value) {
    localConversationList = localConversationList.filter(
      c => !isGroupConversation(c)
    );
  }

  // Una sola tarjeta por cliente (Victor 2026-05-23): agrupa TODAS las
  // conversaciones del mismo contacto en una sola tarjeta, sin importar a qué
  // número/sucursal escribió. Sin duplicados en la lista.
  localConversationList = collapseByContact(localConversationList);

  // Orden por ÚLTIMO mensaje desc (Victor 2026-05-16): cuando un cliente
  // escribe, su conversación salta al tope. Se recalcula solo al llegar
  // mensajes. Solo cuando el sort activo es el por defecto (actividad reciente);
  // si el usuario elige otro orden con el control ↑↓, se respeta el del store.
  if (
    activeSortBy.value === wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
  ) {
    // Más reciente arriba: el que escribió hace menos tiempo va primero.
    localConversationList = [...localConversationList].sort(
      (a, b) => getLastMsgTs(b) - getLastMsgTs(a)
    );
  } else if (
    activeSortBy.value === wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_ASC
  ) {
    // Más antigua arriba: el que escribió hace más tiempo va primero · el más
    // reciente queda de último (orden inverso al de arriba).
    localConversationList = [...localConversationList].sort(
      (a, b) => getLastMsgTs(a) - getLastMsgTs(b)
    );
  }

  // Fijar arriba el número personal de Victor (2026-05-22): se mantiene de
  // PRIMERO en LRM/SDE/SDO/STG sin importar el orden ni el filtro de canal ya
  // aplicados. Se hace al final para ganarle a cualquier ordenamiento previo.
  const pinned = [];
  const rest = [];
  localConversationList.forEach(c => {
    const code = getConvBranchInfo(c)?.code;
    if (isPinnedPhone(c.meta?.sender?.phone_number) && isPinnedBranchCode(code)) {
      pinned.push(c);
    } else {
      rest.push(c);
    }
  });

  return pinned.length ? [...pinned, ...rest] : localConversationList;
});

// ─────────────────────────────────────────────────────────────────────
// Tab "Chats Urgentes" · vista AGRUPADA por sucursal → agente (Q1 · 2026-05-12)
// Lista cross-branch (urgent_index) · branches y agents colapsables independientes
// con localStorage. Orden: PERS primero → resto por código alfabético; agentes
// alfabético · "Sin asignar" al final. Branches con 0 urgentes se ocultan.
// ─────────────────────────────────────────────────────────────────────
const URGENT_COLLAPSED_BRANCHES_KEY = 'victorpos:urgent-collapsed-branches';
const URGENT_COLLAPSED_AGENTS_KEY = 'victorpos:urgent-collapsed-agents';
const collapsedUrgentBranches = ref(new Set(
  (() => {
    try { return JSON.parse(localStorage.getItem(URGENT_COLLAPSED_BRANCHES_KEY) || '[]'); }
    catch (_) { return []; }
  })()
));
const collapsedUrgentAgents = ref(new Set(
  (() => {
    try { return JSON.parse(localStorage.getItem(URGENT_COLLAPSED_AGENTS_KEY) || '[]'); }
    catch (_) { return []; }
  })()
));

function getAgentInitials(name) {
  if (!name) return '??';
  return name.trim().split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase() || '').join('') || '??';
}

// ─────────────────────────────────────────────────────────────────────
// Listas por sucursal en despliegue (Victor 2026-06-07): el chat se agrupa en
// acordeones por sucursal, CERRADOS por defecto, se abren al clic (se recuerda
// el estado). En la cuenta personal/master → 5 listas (Mi personal + 4); en las
// sucursales → 4 listas (solo la activa trae clientes, las demás vacías).
// ─────────────────────────────────────────────────────────────────────
const SUCURSAL_SECTIONS = [
  { key: 'sdo', name: 'Sucursal SDO', codes: ['SDO'] },
  { key: 'stg', name: 'Sucursal Santiago De Los Caballeros', codes: ['STG'] },
  { key: 'rom', name: 'Sucursal La Romana', codes: ['ROM', 'LRM'] },
  { key: 'sde', name: 'Sucursal SDE', codes: ['SDE'] },
];
const branchSectionsDef = computed(() =>
  isPersonalBranchActive.value
    ? [{ key: 'personal', name: 'Mi personal', isPersonal: true }, ...SUCURSAL_SECTIONS]
    : [...SUCURSAL_SECTIONS]
);
// Agrupar el chat por sucursal aplica a las pestañas de conversaciones (no en
// búsqueda, ni en Tareas/Leads asignados que tienen su propia vista).
const branchGroupingActive = computed(
  () =>
    !searchQuery.value.trim() &&
    activeAssigneeTab.value !== 'tasks' &&
    activeAssigneeTab.value !== 'assigned_leads'
);
// Carga sin brincos · P3 (Victor 2026-06-08): mientras NO se han cargado TODAS
// las conversaciones, los conteos por sucursal saltarían (25→119…). Por eso los
// congelamos en "···" y mostramos una barrita dorada sutil arriba; al terminar
// (hasCurrentPageEndReached) aparecen los conteos reales y la barra desaparece.
const fullListLoading = computed(
  () => branchGroupingActive.value && !hasCurrentPageEndReached.value
);
const CHAT_EXPANDED_BRANCHES_KEY = 'victorpos:chat-expanded-branches';
const expandedChatBranches = ref(
  new Set(
    (() => {
      try { return JSON.parse(localStorage.getItem(CHAT_EXPANDED_BRANCHES_KEY) || '[]'); }
      catch (_) { return []; }
    })()
  )
);
function isChatBranchExpanded(key) {
  return expandedChatBranches.value.has(key);
}
function toggleChatBranch(key) {
  const next = new Set(expandedChatBranches.value);
  const expanding = !next.has(key);
  if (expanding) next.add(key); else next.delete(key);
  expandedChatBranches.value = next;
  try { localStorage.setItem(CHAT_EXPANDED_BRANCHES_KEY, JSON.stringify([...next])); } catch (_) {}
  // Al ABRIR una sucursal (Victor 2026-06-11): reanuda en segundo plano la carga
  // que el arranque dejó en pausa (solo se había traído el 1er lote chico), para
  // que los clientes de esa sucursal terminen de entrar en silencio.
  if (expanding && !hasCurrentPageEndReached.value) {
    nextTick(() => loadMoreConversations());
  }
  // Red de seguridad (Victor 2026-06-10): al abrir/cerrar una sucursal cambian
  // las filas en cada posición; un toque al scroll obliga al virtualizador a
  // re-medir y reposicionar de inmediato, sin esperar el ResizeObserver.
  nextTick(() => { try { virtualListRef.value?.scrollBy?.(0); } catch (_) {} });
}
const activeSectionKey = computed(() => {
  if (isPersonalBranchActive.value) return 'personal';
  const code = (activeBranchRecord.value?.code || '').toUpperCase();
  return SUCURSAL_SECTIONS.find(s => s.codes.includes(code))?.key || null;
});
function sectionKeyForConv(conv) {
  // Cada conversación va SIEMPRE a su sucursal real (Victor 2026-06-08): los de
  // SDO en SDO, los de SDE en SDE, La Romana en La Romana, Santiago en Santiago,
  // sin importar qué sucursal esté activa. Antes se amontonaba TODO bajo la
  // sucursal activa. Lo personal/desconocido: en la vista personal → "Mi personal";
  // en una sucursal → la lista activa (lo personal sin sección visible se omite).
  const info = getConvBranchInfo(conv);
  if (info.isPersonal) return 'personal';
  const sec = SUCURSAL_SECTIONS.find(s => s.codes.includes(info.code));
  if (sec) return sec.key;
  return isPersonalBranchActive.value ? 'personal' : activeSectionKey.value;
}

// Una sola tarjeta por (SUCURSAL + contacto) · Victor 2026-05-23. Cada sucursal
// es independiente: el mismo cliente que escribe a varias sucursales aparece una
// vez POR sucursal. Pero dentro de una misma sucursal (aunque tenga 2 números o
// varias conversaciones del mismo cliente) se ve UNA sola tarjeta. La
// representativa = la conversación más reciente · unread = suma · _collapsedNumbers
// = últimos 4 dígitos de cada número distinto de esa sucursal (para los badges).
function collapseByContact(list) {
  const groups = new Map();
  const out = [];
  list.forEach(c => {
    // Identidad por TELÉFONO normalizado (últimos 10 dígitos), no por contact_id:
    // así un mismo número con varios contactos DUPLICADOS en BD se colapsa igual.
    // Si no hay teléfono, cae a contact_id (no rompe los casos sin número).
    const phone = (c.meta?.sender?.phone_number || '').replace(/\D/g, '').slice(-10);
    const identity = phone || c.meta?.sender?.id;
    if (!identity) {
      out.push(c);
      return;
    }
    const inbox = store.getters['inboxes/getInbox'](c.inbox_id);
    const branchId = inbox?.branch_id ?? c.branch_id ?? 'sb';
    const key = `${branchId}:${identity}`;
    const g = groups.get(key);
    if (g) g.push(c);
    else groups.set(key, [c]);
  });
  groups.forEach(convs => {
    if (convs.length === 1) {
      out.push(convs[0]);
      return;
    }
    const rep = [...convs].sort((a, b) => getLastMsgTs(b) - getLastMsgTs(a))[0];
    const unread = convs.reduce((s, c) => s + (c.unread_count || 0), 0);
    const numbers = [
      ...new Set(
        convs
          .map(c =>
            inboxPhoneDigits(store.getters['inboxes/getInbox'](c.inbox_id) || {})
          )
          .filter(Boolean)
          .map(d => d.slice(-4))
      ),
    ];
    out.push({
      ...rep,
      unread_count: unread,
      _collapsedCount: convs.length,
      _collapsedNumbers: numbers,
    });
  });
  return out;
}

function getLastMsgTs(conv) {
  if (!conv) return 0;
  // El MÁS reciente entre: último mensaje real, última actividad y timestamp de
  // la conversación (Victor 2026-05-22). Cuando un cliente escribe, alguno de
  // estos campos se actualiza; tomar el máximo hace que la conversación salte a
  // su posición correcta sin importar cuál de ellos cambió.
  let t = 0;
  [
    conv.last_non_activity_message?.created_at,
    conv.last_activity_at,
    conv.timestamp,
  ].forEach(c => {
    let v = Number(c) || 0;
    if (v && v < 1e12) v *= 1000; // segundos → ms
    if (v > t) t = v;
  });
  return t;
}

// R3 · 2026-05-12 · ¿Conv está activamente siendo atendida? (independiente del viewer)
// Si true → va al final del grupo del agente, con separator "EN PROCESO".
function hasActiveAttending(conv) {
  const a = conv?.additional_attributes || {};
  if (a.attending_reclaimed_at) return false;
  if (!a.attending_user_id || !a.attending_since) return false;
  return (Date.now() - Number(a.attending_since) * 1000) < (30 * 60 * 1000);
}

// Q1 v3 · 2026-05-12 · Si el responsable reclamó Y yo soy el ex-atendedor (la
// que estaba ayudando) → la conv desaparece de mi tab Urgentes. La razón:
// el responsable ya está disponible, no tengo que seguir monitoreándola.
// Excepción: si yo soy también el responsable, no aplica (raro pero seguro).
function isReclaimedFromMe(conv, myId) {
  if (!myId) return false;
  const a = conv?.additional_attributes || {};
  if (!a.attending_reclaimed_at) return false;
  if (conv?.meta?.assignee?.id === myId) return false;
  return Number(a.attending_user_id) === Number(myId);
}

// Q1 v4 · 2026-05-12 · Sistema "una conv en un solo tab a la vez" (regla Victor).
// - isMineAndReclaimed: soy responsable Y ya reclamé · sale de Urgentes, vuelve a Mi Chat.
// - isExcludedFromMine: soy responsable Y la conv está urgente Y no he reclamado todavía
//   · sale de Mi Chat, vive en Urgentes hasta que la reclame o respondan.
function isMineAndReclaimed(conv, myId) {
  if (!myId) return false;
  if (conv?.meta?.assignee?.id !== myId) return false;
  return !!conv?.additional_attributes?.attending_reclaimed_at;
}
function isExcludedFromMine(conv, myId) {
  if (!myId) return false;
  if (conv?.meta?.assignee?.id !== myId) return false;
  // Reclamada → siempre vuelve a Mi Chat aunque el tiempo siga "urgente".
  if (conv?.additional_attributes?.attending_reclaimed_at) return false;
  return isUrgentConversation(conv) || isReclaimableByMe(conv, myId);
}

// Helper · resolver branch de una conv. Prioridad:
// 1. branches_meta del endpoint urgent_index (incluye TODAS las branches sin filtro)
// 2. Store branches (filtrado por accessible_branch_ids del agente)
// 3. Inbox.branch_id como fallback
function getConvBranchInfo(conv) {
  // branches_meta de CUALQUIER endpoint que lo mande (urgent_index o el index
  // normal vía conversationStats): incluye TODAS las sucursales sin filtrar por
  // acceso → permite clasificar un chat asignado cross-sucursal en SU sucursal
  // real aunque el agente no tenga acceso a esa sucursal (Victor 2026-06-08).
  const meta = {
    ...(store.getters['conversationStats/getBranchesMeta'] || {}),
    ...(urgentBranchesMeta.value || {}),
  };
  const branches = store.getters['branches/getBranches'] || [];
  const inboxesById = store.getters['inboxes/getInboxes'] || [];
  let branchId = conv?.branch_id;
  if (!branchId && conv?.inbox_id) {
    const inb = inboxesById.find?.(i => i.id === conv.inbox_id);
    branchId = inb?.branch_id;
  }
  if (!branchId) return { id: 'no-branch', code: 'SIN', name: 'Sin sucursal', isPersonal: false };
  // Prioridad 1: branches_meta del endpoint (siempre completo)
  const metaEntry = meta[branchId] || meta[String(branchId)];
  if (metaEntry) {
    return {
      id: branchId,
      code: (metaEntry.code || metaEntry.name?.substring(0, 3) || 'OTR').toUpperCase(),
      name: metaEntry.name || metaEntry.code || 'Sucursal',
      isPersonal: !!metaEntry.is_personal,
    };
  }
  // Prioridad 2: Store (puede estar filtrado por permisos del agente)
  const branch = branches.find(b => b.id === branchId);
  if (!branch) return { id: 'no-branch', code: 'SIN', name: 'Sin sucursal', isPersonal: false };
  return {
    id: branch.id,
    code: (branch.code || branch.name?.substring(0, 3) || 'OTR').toUpperCase(),
    name: branch.name || branch.code || 'Sucursal',
    isPersonal: !!branch.is_personal,
  };
}

const urgentGroupedByBranchThenAgent = computed(() => {
  if (activeAssigneeTab.value !== 'urgent') return [];
  void urgentTick.value;
  const myId = currentUser.value?.id;
  // Source: cross-branch list si está cargada, fallback a conversationList (regular store)
  const source = urgentConversationsAll.value.length > 0
    ? urgentConversationsAll.value
    : (conversationList.value || []);
  // V2 · 2026-05-14 (Victor): SOLO mis convs asignadas · ya no chats de otros
  const filtered = source.filter(
    c => c?.meta?.assignee?.id === myId
      && (isUrgentConversation(c) || isReclaimableByMe(c, myId))
      && !isReclaimedFromMe(c, myId)
      && !isMineAndReclaimed(c, myId)
  );

  // V2 · 2026-05-14 (Victor) · ahora todas las convs son MÍAS · agrupamos solo
  // por sucursal · ya no hay sub-grupo por agente (sería redundante).
  const branchMap = new Map();
  filtered.forEach(c => {
    const b = getConvBranchInfo(c);
    if (!branchMap.has(b.id)) {
      branchMap.set(b.id, {
        branchKey: `branch-${b.id}`,
        branchCode: b.code,
        branchName: b.name,
        isPersonal: b.isPersonal,
        isNoBranch: b.id === 'no-branch',
        conversations: [],
        totalCount: 0,
      });
    }
    const branchGroup = branchMap.get(b.id);
    branchGroup.conversations.push(c);
    branchGroup.totalCount += 1;
  });

  // V2 · split direct por branch: available (sin atender) vs inProcess (siendo
  // atendidas) · sorted por last_msg_ts desc · el template ya no itera agents.
  const branches = Array.from(branchMap.values()).map(bg => {
    const sorted = [...bg.conversations].sort((x, y) => getLastMsgTs(y) - getLastMsgTs(x));
    bg.available = sorted.filter(c => !hasActiveAttending(c));
    bg.inProcess = sorted.filter(c => hasActiveAttending(c));
    bg.conversations = sorted;
    // Mantener `agents` como array de un solo elemento por compat retrocompat
    // del template antiguo (en caso de fallback) · no se renderiza en V2.
    bg.agents = [];
    return bg;
  });

  // Ordenar branches · PERS primero → resto alfabético por código → "Sin sucursal" al final
  branches.sort((a, b) => {
    if (a.isNoBranch && !b.isNoBranch) return 1;
    if (!a.isNoBranch && b.isNoBranch) return -1;
    if (a.isPersonal && !b.isPersonal) return -1;
    if (!a.isPersonal && b.isPersonal) return 1;
    return a.branchCode.localeCompare(b.branchCode);
  });

  return branches.filter(b => b.totalCount > 0);
});

function isUrgentBranchExpanded(branchKey) {
  return !collapsedUrgentBranches.value.has(branchKey);
}
function toggleUrgentBranch(branchKey) {
  const next = new Set(collapsedUrgentBranches.value);
  if (next.has(branchKey)) next.delete(branchKey); else next.add(branchKey);
  collapsedUrgentBranches.value = next;
  try { localStorage.setItem(URGENT_COLLAPSED_BRANCHES_KEY, JSON.stringify([...next])); } catch (_) {}
}
function isUrgentAgentExpanded(agentKey) {
  return !collapsedUrgentAgents.value.has(agentKey);
}
function toggleUrgentAgent(agentKey) {
  const next = new Set(collapsedUrgentAgents.value);
  if (next.has(agentKey)) next.delete(agentKey); else next.add(agentKey);
  collapsedUrgentAgents.value = next;
  try { localStorage.setItem(URGENT_COLLAPSED_AGENTS_KEY, JSON.stringify([...next])); } catch (_) {}
}

// Pestañas de departamento (Victor 2026-06-12): Clientes/Selección/Retoque/
// Impresión/Álbum traen su propia lista (clientesConversations…) por fuera de la
// lista global de Vuex. Por eso NO dependen de chatListLoading ni se paginan con
// scroll infinito — excluirlas del spinner, del EOF y del IntersectionObserver,
// igual que 'urgent'/'tasks'. Si no, el spinner de la lista global gira eterno.
const isDepartmentTab = computed(() =>
  ['seleccion', 'retoque', 'impresion', 'album', 'clientes'].includes(
    activeAssigneeTab.value
  )
);

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { order_by: orderBy } = filterBy;
  // Status SIEMPRE Abiertas (Victor 2026-05-30): unifica el conteo entre
  // computadoras. Antes uiSettings recordaba 'all' o 'resolved' por usuario
  // y un agente veía 1,173 mientras otro veía 209 — inconsistente. Ahora
  // todos arrancan en Abiertas; el tab "Cerrados" pasa a 'resolved'
  // temporalmente mientras esté activo (ver onClickTabChange más abajo).
  activeStatus.value = wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
  // El canal NO se restaura de uiSettings (Victor 2026-05-25): un canal "pegado"
  // dejaba "Todos" vacío al recargar — si la sucursal activa no tiene inboxes de
  // ese canal, el filtro server-side mandaba inbox_id=[-1] y ocultaba todo. El
  // filtro de canal es ahora per-sesión y arranca SIEMPRE en 'all'.
  activeChannel.value = 'all';
}

function emitConversationLoaded() {
  emit('conversationLoad');
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  store.dispatch('customViews/update', payloadData);
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function loadMoreConversations() {
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  // Vista por sucursal (Victor 2026-06-11): NO jalar las ~930 abiertas de golpe
  // mientras TODO está colapsado. Con la 1ra página bastan cabeceras + conteos
  // reales del servidor. Al abrir una sucursal (toggleChatBranch) se reanuda la
  // carga en segundo plano. Esto elimina el brinco y el spinner del arranque.
  if (
    branchGroupingActive.value &&
    expandedChatBranches.value.size === 0 &&
    currentPage.value >= 1
  ) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Use IntersectionObserver instead of @scroll since Virtualizer only emits on user scroll.
// If the list doesn’t fill the viewport, loading can stall.
// IntersectionObserver triggers as soon as the sentinel is visible.
const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

function updateAssigneeTab(selectedTab) {
  // Cerrar el chat abierto al tocar cualquier pestaña (Victor 2026-06-09): si hay
  // una conversación seleccionada, vuelve a la lista ("Selecciona un chat") para no
  // dejar el chat viejo abierto al cambiar de carpeta. Aplica aunque se toque la
  // misma pestaña activa (también vuelve a la lista).
  if (route.params.conversation_id || route.params.conversationId) {
    redirectToConversationList();
  }
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    // Limpiar filtro de agentes al salir del tab Leads Asignados
    if (activeAssigneeTab.value === 'assigned_leads' && selectedTab !== 'assigned_leads') {
      selectedAssigneeIds.value = [];
    }
    // Tab "Cerrados" (L1 · M1.B): cambia status a 'resolved' y refetch limpio.
    // Al salir del tab, restaurar status a 'open' (también con refetch).
    const isEnteringClosed = selectedTab === 'closed';
    const isLeavingClosed = activeAssigneeTab.value === 'closed' && selectedTab !== 'closed';
    if (isEnteringClosed) {
      activeStatus.value = wootConstants.STATUS_TYPE.RESOLVED;
    } else if (isLeavingClosed) {
      activeStatus.value = wootConstants.STATUS_TYPE.OPEN;
    }
    activeAssigneeTab.value = selectedTab;
    // Al salir de "Grupos" se limpia la carpeta activa para no arrastrar el
    // filtro a otras pestañas (Victor 2026-06-05 · P2).
    if (selectedTab !== 'mygroups') activeGroupFolder.value = null;
    // Sub-chip de sucursal de Impresión/Álbum: se reinicia a "todas" al cambiar
    // de pestaña (Victor 2026-06-11).
    activeDeptBranch.value = null;
    if (isEnteringClosed || isLeavingClosed) {
      resetAndFetchData();
    } else if (!currentPage.value) {
      fetchConversations();
    }
  }
}

function onBasicFilterChange(value, type) {
  // El filtro de CANAL ahora recarga server-side: resuelve el canal a sus
  // inbox_ids (channelInboxIds) y el servidor pagina solo esos inboxes. Esto
  // elimina el spinner pegado y la paginación masiva del filtrado client-side
  // anterior (Victor 2026-05-24).
  if (type === 'channel') {
    activeChannel.value = value;
    store.dispatch('setChatChannelFilter', value);
  } else if (type === 'status') {
    activeStatus.value = value;
  } else if (type === 'sort') {
    activeSortBy.value = value;
  }
  resetAndFetchData();
}

// Búsqueda global por Enter REMOVIDA (Victor 2026-05-21): el buscador global del
// servidor NO respeta la sucursal y permitía a un agente encontrar y abrir
// conversaciones de otra sucursal (incluido el número personal) y responder como
// si fueran ese número. Ahora la búsqueda solo filtra inline la lista del agente
// (branch-scoped). El Enter ya no navega a ningún modal.

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = 'mention';
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = 'unattended';
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function assignPriority(priority, conversationId = null) {
  store.dispatch('setCurrentChatPriority', {
    priority,
    conversationId,
  });
  store.dispatch('assignPriority', { conversationId, priority }).then(() => {
    useTrack(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
      newValue: priority,
      from: 'Context menu',
    });
    useAlert(
      t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
        priority,
        conversationId,
      })
    );
  });
}

async function markAsUnread(conversationId) {
  try {
    await store.dispatch('markMessagesUnread', {
      id: conversationId,
    });
    redirectToConversationList();
  } catch (error) {
    // Ignore error
  }
}
async function markAsRead(conversationId) {
  try {
    await store.dispatch('markMessagesRead', {
      id: conversationId,
    });
  } catch (error) {
    // Ignore error
  }
}

async function onAssignTeam(team, conversationId = null) {
  try {
    await store.dispatch('assignTeam', {
      conversationId,
      teamId: team.id,
    });
    useAlert(
      t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL', {
        team: team.name,
        conversationId,
      })
    );
  } catch (error) {
    useAlert(t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'));
  }
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  store.dispatch('toggleStatus', payload).then(() => {
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
});

// Barra inferior móvil (Victor 2026-06-02): el botón "Tareas" navega a Chats y
// emite esto para abrir la pestaña de tareas directamente.
useEmitter('switch-assignee-tab', tab => {
  updateAssigneeTab(tab);
});

onMounted(() => {
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  store.dispatch('setChatChannelFilter', activeChannel.value);
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

// Tick para tab "Chats Urgentes" · refresca conteo y filtro cada 30s sin reload.
// También dispara fetch del endpoint cross-branch urgent_index (Q1 · 2026-05-12).
let urgentTickTimer = null;
onMounted(() => {
  fetchUrgentConversationsAll();
  fetchTasks();
  // Conteos de departamentos listos sin entrar a la pestaña.
  if (hasSeleccionRole.value || hasImpresionRole.value || hasClientesRole.value) fetchDepartmentConversations();
  urgentTickTimer = setInterval(() => {
    urgentTick.value += 1;
    fetchUrgentConversationsAll();
    if (hasSeleccionRole.value || hasImpresionRole.value || hasClientesRole.value) fetchDepartmentConversations();
  }, 30000);
});
onBeforeUnmount(() => {
  if (urgentTickTimer) clearInterval(urgentTickTimer);
});

// Q1 v2 (2026-05-12 PM): fetch inmediato al cambiar al tab Urgentes para evitar
// flash de "0 clientes" durante la ventana de hasta 30s entre ticks. También
// dispara cuando la ventana del navegador recupera foco (visibilitychange).
watch(activeAssigneeTab, (newTab) => {
  if (newTab === 'urgent') fetchUrgentConversationsAll();
  if (newTab === 'tasks') fetchTasks();
  if (['seleccion', 'retoque', 'impresion', 'album', 'clientes'].includes(newTab)) fetchDepartmentConversations();
});
function onVisibilityChange() {
  if (!document.hidden && activeAssigneeTab.value === 'urgent') {
    fetchUrgentConversationsAll();
  }
}
onMounted(() => { document.addEventListener('visibilitychange', onVisibilityChange); });
onBeforeUnmount(() => { document.removeEventListener('visibilitychange', onVisibilityChange); });

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

// === Fijar chat (estilo WhatsApp · Victor 2026-06-02) ===
// Máx 5 fijados, guardados por USUARIO en uiSettings (se sincronizan al
// servidor → mismo set en celular y PC). Los fijados suben al tope de la lista.
const MAX_USER_PINS = 5;
const userPinnedIds = computed(
  () => uiSettings.value.user_pinned_conversation_ids || []
);
function isUserPinned(id) {
  return userPinnedIds.value.includes(id);
}
function toggleUserPin(conversationId) {
  const current = [...userPinnedIds.value];
  const idx = current.indexOf(conversationId);
  if (idx > -1) {
    current.splice(idx, 1);
  } else {
    if (current.length >= MAX_USER_PINS) {
      useAlert(`Solo puedes fijar ${MAX_USER_PINS} chats. Desfija uno primero.`);
      return;
    }
    current.unshift(conversationId);
  }
  updateUISettings({ user_pinned_conversation_ids: current });
}
// === Carpetas de Grupos (Victor 2026-06-05 · P2 "chips") ===
// Carpetas creadas por el usuario para agrupar los chats de grupo. Se guardan
// por USUARIO en uiSettings (group_folders + group_folder_map identifier→id) y
// se sincronizan cel↔PC igual que los fijados. La barra de chips solo aparece en
// la pestaña "Grupos" de la sucursal Personal.
const groupFolders = computed(() => uiSettings.value.group_folders || []);
const groupFolderMap = computed(() => uiSettings.value.group_folder_map || {});
const activeGroupFolder = ref(null); // null = "Todos"
const showGroupFolderModal = ref(false);

const groupConversations = computed(() =>
  (store.getters['getAllConversations'] || []).filter(
    c => c.status === 'open' && isGroupConversation(c)
  )
);
// Consistencia automática de carpetas de grupos (Victor 2026-06-06): un grupo
// pertenece a una carpeta si está asignado a mano (group_folder_map) O si su
// NOMBRE contiene el nombre de la carpeta (sin tildes/mayúsculas). Así "Oficial
// Equipo SDE" cae solo en "Oficial" y "Contabilidad …" en "Contabilidad", sin
// tener que asignarlos uno por uno. Misma lógica en celular y PC.
function normalizeFolderText(s) {
  return (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
function groupMatchesFolder(identifier, name, folderId) {
  if (groupFolderMap.value[identifier] === folderId) return true;
  const folder = groupFolders.value.find(f => f.id === folderId);
  if (!folder || !folder.name) return false;
  return normalizeFolderText(name).includes(normalizeFolderText(folder.name));
}
const groupFolderCounts = computed(() => {
  const counts = {};
  groupConversations.value.forEach(c => {
    const id = c.meta?.sender?.identifier;
    const name = c.meta?.sender?.name;
    groupFolders.value.forEach(f => {
      if (groupMatchesFolder(id, name, f.id)) {
        counts[f.id] = (counts[f.id] || 0) + 1;
      }
    });
  });
  return counts;
});
const groupListForFolders = computed(() =>
  groupConversations.value
    .map(c => ({
      identifier: c.meta?.sender?.identifier,
      name: c.meta?.sender?.name || `Grupo ${c.id}`,
    }))
    .filter(g => g.identifier)
);

function selectGroupFolder(id) {
  activeGroupFolder.value = id;
}
function createGroupFolder(name) {
  const folders = [...groupFolders.value, { id: `gf_${Date.now()}`, name }];
  updateUISettings({ group_folders: folders });
}
function renameGroupFolder({ id, name }) {
  const folders = groupFolders.value.map(f => (f.id === id ? { ...f, name } : f));
  updateUISettings({ group_folders: folders });
}
function deleteGroupFolder(id) {
  const folders = groupFolders.value.filter(f => f.id !== id);
  const map = { ...groupFolderMap.value };
  Object.keys(map).forEach(k => {
    if (map[k] === id) delete map[k];
  });
  if (activeGroupFolder.value === id) activeGroupFolder.value = null;
  updateUISettings({ group_folders: folders, group_folder_map: map });
}
function assignGroupToFolder({ identifier, folderId }) {
  const map = { ...groupFolderMap.value };
  if (folderId) {
    map[identifier] = folderId;
  } else {
    delete map[identifier];
  }
  updateUISettings({ group_folder_map: map });
}

// Lista con los fijados ordenados arriba (no toca conversationList ni la lógica
// de selección; solo el orden de render).
const displayConversationList = computed(() => {
  const list = conversationList.value || [];
  const pinned = userPinnedIds.value;
  if (!pinned.length) return list;
  const pinnedItems = [];
  const rest = [];
  list.forEach(c => {
    if (pinned.includes(c.id)) pinnedItems.push(c);
    else rest.push(c);
  });
  pinnedItems.sort((a, b) => pinned.indexOf(a.id) - pinned.indexOf(b.id));
  return [...pinnedItems, ...rest];
});

// Filas a renderizar en el Virtualizer. Si la agrupación por sucursal está activa,
// intercala encabezados de sección (acordeón) con los chats de las secciones
// abiertas. Si no (búsqueda/tareas), devuelve la lista plana de siempre.
// Conteo real del servidor mapeado a cada sección de sucursal (Victor 2026-06-08).
// branch_counts = { branch_id: total } · branchesMeta = { branch_id: {code} }.
// Se traduce branch_id -> código -> sección. Vacío hasta que llega la 1ra respuesta.
const branchRealCounts = computed(() => {
  const out = {};
  const bMeta = store.getters['conversationStats/getBranchesMeta'] || {};
  const bCounts = store.getters['conversationStats/getBranchCounts'] || {};
  Object.keys(bCounts).forEach(bid => {
    const m = bMeta[bid] || bMeta[String(bid)];
    const code = (m?.code || '').toUpperCase();
    // "Mi personal" (Victor 2026-06-11): también sale del conteo real del servidor
    // para que NO salte de 0 a 186 al ir cargando lotes. Antes no se mapeaba y caía
    // al conteo de lo cargado, que con el primer lote chico saldría mal.
    if (m?.is_personal) { out.personal = (out.personal || 0) + (bCounts[bid] || 0); return; }
    const sec = SUCURSAL_SECTIONS.find(s => s.codes.includes(code));
    if (sec) out[sec.key] = (out[sec.key] || 0) + (bCounts[bid] || 0);
  });
  return out;
});

// ¿Ya llegó el conteo real del servidor? Mientras NO, durante la 1ra carga se
// muestra "···" en el encabezado; al llegar (1ra respuesta) salen los reales.
const branchCountsReady = computed(
  () => Object.keys(branchRealCounts.value).length > 0
);

const branchRenderRows = computed(() => {
  if (!branchGroupingActive.value) return displayConversationList.value;
  const sections = branchSectionsDef.value;
  const buckets = new Map();
  sections.forEach(s => buckets.set(s.key, []));
  (displayConversationList.value || []).forEach(c => {
    const key = sectionKeyForConv(c);
    if (key && buckets.has(key)) buckets.get(key).push(c);
  });
  // Conteo REAL por sucursal del servidor (Victor 2026-06-08): el encabezado usa
  // este total —no "lo cargado"— así sale completo de una vez y no baila al
  // refrescar. Si aún no llegó (primer pintado/búsqueda) cae a lo cargado.
  const real = branchRealCounts.value;
  // Mi Chat / Urgentes (Victor 2026-06-09): estas pestañas muestran SOLO las
  // tarjetas del agente (sus asignados / sus urgentes), ya deduplicadas por cliente.
  // El encabezado debe contar ESAS tarjetas listadas —no el total de "Todos" del
  // servidor— para que el número cuadre con lo que se ve. "Todos" sigue usando el
  // conteo real del servidor (no depende de cuánto se haya cargado).
  // Filtros de departamento (Victor 2026-06-12): el encabezado de cada sucursal
  // cuenta las tarjetas LISTADAS de ese departamento —no el total de la sucursal—
  // para que al hacer clic en Clientes/Selección/Retoque/Impresión/Álbum los
  // conteos por sucursal bajen a la realidad (solo los clientes de ese depto).
  const useLoadedCount =
    activeAssigneeTab.value === 'me' ||
    activeAssigneeTab.value === 'urgent' ||
    ['seleccion', 'retoque', 'impresion', 'album', 'clientes'].includes(
      activeAssigneeTab.value
    );
  const countFor = s =>
    !useLoadedCount && s.key in real
      ? real[s.key]
      : (buckets.get(s.key) || []).length;
  // Proporción de cada sucursal vs la de mayor conteo (barra de fondo V2 · Victor 2026-06-08).
  const maxCount = Math.max(1, ...sections.map(countFor));
  const rows = [];
  sections.forEach(s => {
    const convs = buckets.get(s.key) || [];
    const expanded = isChatBranchExpanded(s.key);
    const count = countFor(s);
    rows.push({
      __branchHeader: true,
      id: `__bh_${s.key}`,
      key: s.key,
      name: s.name,
      isPersonal: !!s.isPersonal,
      count,
      proportion: count / maxCount,
      expanded,
    });
    if (expanded) rows.push(...convs);
  });
  return rows;
});

// Llave de remonte del virtualizador (Victor 2026-06-11 · fix solapamiento):
// al abrir/cerrar una sucursal, en algunas posiciones cambia el TIPO de fila
// (encabezado ↔ tarjeta). virtua recicla por índice y reusaba la altura vieja →
// la 1ª tarjeta se montaba sobre el encabezado. Remontar el virtualizador en cada
// cambio del set de sucursales abiertas le da una medición limpia desde cero.
const branchVirtualKey = computed(() =>
  branchGroupingActive.value
    ? `bg|${[...expandedChatBranches.value].sort().join(',')}`
    : 'flat'
);

// Color del puntito por sucursal (encabezado P2 · Victor 2026-06-11).
// Colores oficiales de sucursal (helper compartido · Victor 2026-06-11): mismos
// colores del sistema de facturación, usados también en los chips del Kanban.
function branchDotColor(key) {
  return branchColor(key);
}

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('toggleUserPin', toggleUserPin);
provide('isUserPinned', isUserPinned);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('assignPriority', assignPriority);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});

// V5-P1 · 2026-05-15 (Victor) · Chatlist SIEMPRE abierto (sin rail collapse)
// Funciona side-by-side con la conversación · ambos layouts.
const shouldCollapseRail = computed(() => false);

// V4 · Avatar Strip · 2026-05-13 (Victor)
// Cuando el rail está colapsado mostramos la columna de avatares (uno por chat).
// Click en avatar → cambia conversación · hover sobre wrapper → expande full list.
const railChannelCode = chat => {
  const inbox = store.getters['inboxes/getInbox'](chat.inbox_id) || {};
  const t = (inbox.channel_type || '').toLowerCase();
  const name = (inbox.name || '').toLowerCase();
  if (t.includes('whatsapp')) return 'wa';
  if (t.includes('instagram')) return 'ig';
  if (t.includes('facebook') || t.includes('messenger')) return 'fb';
  if (t.includes('tiktok')) return 'tk';
  if (t.includes('twitter')) return 'tw';
  if (t.includes('email')) return 'email';
  if (t.includes('sms') || t.includes('twilio')) return 'sms';
  if (t.includes('telegram')) return 'tg';
  if (t.includes('line')) return 'line';
  if (t.includes('api') || t === '') {
    if (name.includes('whatsapp') || name.includes('wa ') || name === 'wa') return 'wa';
    if (name.includes('tiktok') || name.includes('tk ')) return 'tk';
    if (name.includes('instagram') || name.includes(' ig ')) return 'ig';
    if (name.includes('messenger') || name.includes('facebook')) return 'fb';
  }
  return 'web';
};

const railContactName = chat => {
  const senderId = chat?.meta?.sender?.id;
  const contact = senderId ? store.getters['contacts/getContact'](senderId) : null;
  return contact?.name || chat?.meta?.sender?.name || `Lead #${chat?.id || ''}`;
};

const railContactInitial = chat => {
  const name = railContactName(chat);
  return (name || '?').trim().charAt(0).toUpperCase();
};

const railContactThumbnail = chat => {
  const senderId = chat?.meta?.sender?.id;
  const contact = senderId ? store.getters['contacts/getContact'](senderId) : null;
  return contact?.thumbnail || chat?.meta?.sender?.thumbnail || '';
};

const railIsActive = chat => {
  return String(chat?.id) === String(route.params.conversation_id);
};

const railOpenChat = (chat, e) => {
  if (!chat?.id) return;
  const path = frontendURL(
    conversationUrl({
      accountId: currentAccountId.value,
      activeInbox: activeInbox.value || null,
      id: chat.id,
      label: props.label,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
    })
  );
  if (e && (e.metaKey || e.ctrlKey)) {
    e.preventDefault();
    window.open(`${window.chatwootConfig?.hostURL || ''}${path}`, '_blank', 'noopener,noreferrer');
    return;
  }
  router.push({ path });
};
</script>

<template>
  <div
    class="flex flex-col flex-shrink-0 min-w-0 conversations-list-wrap bg-n-surface-1"
    :class="[
      { hidden: !showConversationList, 'oc2-rail': shouldCollapseRail },
      // Celular (Victor 2026-06-02): la lista ocupa el 100% del ancho (en
      // escritorio se queda con su ancho fijo). max-md = < 768px.
      shouldCollapseRail ? '' : 'w-[24%] min-w-[260px] max-md:w-full max-md:min-w-0',
    ]"
  >
    <!-- V4 · Avatar Strip · 2026-05-13 (Victor)
         Cuando rail colapsado mostramos avatares clickeables · al hover wrapper se ocultan
         y aparece el ChatList completo. Cada avatar: foto/inicial + channel badge + mini-dot unread. -->
    <div v-if="shouldCollapseRail" class="oc2-rail-avatars">
      <div class="oc2-rail-avatars-head">
        <svg class="oc2-rail-head-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.8" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
        </svg>
        <div v-if="(conversationStats || {})[activeStatus]" class="oc2-rail-head-count">
          {{ ((conversationStats || {})[activeStatus] || 0).toLocaleString('es-DO') }}
        </div>
      </div>
      <div class="oc2-rail-avatars-list">
        <button
          v-for="chat in displayConversationList"
          :key="`rail-${chat.id}`"
          type="button"
          class="oc2-rail-avatar"
          :class="[`oc2-rail-avatar-${railChannelCode(chat)}`, { 'oc2-rail-avatar-active': railIsActive(chat) }]"
          :title="railContactName(chat)"
          @click="railOpenChat(chat, $event)"
        >
          <img v-if="railContactThumbnail(chat)" :src="railContactThumbnail(chat)" class="oc2-rail-avatar-img" alt="" />
          <span v-else class="oc2-rail-avatar-initial">{{ railContactInitial(chat) }}</span>
          <span class="oc2-rail-avatar-ch" :class="`oc2-rail-avatar-ch-${railChannelCode(chat)}`">
            <svg v-if="railChannelCode(chat) === 'wa'" class="oc2-rail-avatar-ch-svg" viewBox="0 0 32 32">
              <path fill="#fff" d="M27.1 4.9C24.2 1.9 20.2 0.3 16 0.3 7.4 0.3 0.3 7.3 0.3 16c0 2.8 0.7 5.5 2.1 7.9L0.1 31.7l8-2.1c2.3 1.3 4.9 2 7.6 2h0c8.7 0 15.7-7.1 15.7-15.7 0-4.2-1.6-8.1-4.3-11zM16 28.9h0c-2.4 0-4.8-0.6-6.9-1.9l-0.5-0.3-5.1 1.3 1.4-4.9-0.3-0.5C3.3 20.4 2.6 18.2 2.6 16 2.6 8.6 8.6 2.6 16 2.6c3.6 0 7 1.4 9.5 3.9s3.9 5.9 3.9 9.5c0 7.4-6 13.4-13.4 13.4zm7.4-10.1c-0.4-0.2-2.4-1.2-2.8-1.3-0.4-0.1-0.6-0.2-0.9 0.2-0.3 0.4-1 1.3-1.3 1.5-0.2 0.3-0.5 0.3-0.9 0.1-0.4-0.2-1.7-0.6-3.3-2-1.2-1.1-2-2.4-2.3-2.8-0.2-0.4 0-0.6 0.2-0.8 0.2-0.2 0.4-0.5 0.6-0.7 0.2-0.2 0.3-0.4 0.4-0.7 0.1-0.3 0.1-0.5 0-0.7-0.1-0.2-0.9-2.2-1.2-3-0.3-0.8-0.7-0.7-0.9-0.7-0.2 0-0.5 0-0.7 0-0.3 0-0.7 0.1-1 0.5-0.3 0.4-1.3 1.3-1.3 3.2 0 1.9 1.4 3.7 1.5 3.9 0.2 0.3 2.7 4.1 6.5 5.8 0.9 0.4 1.6 0.6 2.2 0.8 0.9 0.3 1.8 0.2 2.4 0.2 0.7-0.1 2.2-0.9 2.5-1.8 0.3-0.9 0.3-1.6 0.2-1.8-0.1-0.2-0.4-0.3-0.7-0.5z"/>
            </svg>
            <svg v-else-if="railChannelCode(chat) === 'ig'" class="oc2-rail-avatar-ch-svg" viewBox="0 0 24 24">
              <path fill="#fff" d="M7.5 2h9C19.985 2 22 4.015 22 6.5v11c0 2.485-2.015 4.5-4.5 4.5h-9C5.015 22 3 19.985 3 17.5v-11C3 4.015 5.015 2 7.5 2zM7 4C5.343 4 4 5.343 4 7v10c0 1.657 1.343 3 3 3h10c1.657 0 3-1.343 3-3V7c0-1.657-1.343-3-3-3H7zm10.5 1.5a1 1 0 1 1 0 2 1 1 0 0 1 0-2zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/>
            </svg>
            <svg v-else-if="railChannelCode(chat) === 'fb'" class="oc2-rail-avatar-ch-svg" viewBox="0 0 24 24">
              <path fill="#fff" d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.19 5.44 3.14 7.18.16.14.26.34.27.55l.05 1.78a.8.8 0 0 0 1.12.71l1.99-.88a.8.8 0 0 1 .54-.04c.91.25 1.88.39 2.89.39 5.64 0 10-4.13 10-9.69C22 6.13 17.64 2 12 2zm6.01 7.4l-2.94 4.66a1.5 1.5 0 0 1-2.16.4l-2.34-1.75a.6.6 0 0 0-.72 0l-3.16 2.39c-.42.32-.97-.18-.7-.62l2.94-4.66a1.5 1.5 0 0 1 2.16-.4l2.34 1.75c.21.16.51.16.72 0l3.16-2.4c.42-.31.97.19.7.63z"/>
            </svg>
            <svg v-else-if="railChannelCode(chat) === 'tk'" class="oc2-rail-avatar-ch-svg oc2-rail-avatar-ch-svg-tk" viewBox="0 0 24 24">
              <path fill="#fff" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.86a8.16 8.16 0 0 0 4.77 1.52V6.94a4.85 4.85 0 0 1-1.84-.25z"/>
            </svg>
          </span>
          <span v-if="chat.unread_count > 0" class="oc2-rail-avatar-unread">
            {{ chat.unread_count > 9 ? '9+' : chat.unread_count }}
          </span>
        </button>
      </div>
    </div>

    <slot />
    <Teleport defer to="#chatModuleTopbar" :disabled="isMobileChat">
    <ChatListHeader
      v-model:search-query="searchQuery"
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !conversationList.length"
      :is-searching="isSearching"
      :search-results-count="searchQuery.trim() ? conversationList.length : null"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
    >
      <!-- P1 "una sola fila" (Victor 2026-06-12): en escritorio los tabs van DENTRO
           del header, entre el brand y el buscador. En celular se quedan en su fila
           (se renderizan en los teleports de abajo gateados por isMobileChat). -->
      <template #tabs>
        <PersonalChatTabs
          v-if="!hasAppliedFiltersOrActiveFolders && isPersonalBranchActive && !isMobileChat"
          :items="assigneeTabItems"
          :active-tab="activeAssigneeTab"
          @chat-tab-change="updateAssigneeTab"
        />
        <ChatTypeTabs
          v-if="!hasAppliedFiltersOrActiveFolders && !isPersonalBranchActive && !isMobileChat"
          :items="assigneeTabItems"
          :active-tab="activeAssigneeTab"
          is-compact
          @chat-tab-change="updateAssigneeTab"
        />
      </template>
    </ChatListHeader>
    </Teleport>

    <!-- Chips de sucursal en celular (Victor 2026-06-02 · Propuesta 3) -->
    <MobileBranchChips />

    <!-- Barra de carga sutil · P3 (Victor 2026-06-08): reemplaza el brinco de
         conteos + el texto grande "Cargando conversaciones". -->
    <div
      v-if="fullListLoading && !isDepartmentTab"
      class="h-0.5 shrink-0 bg-gradient-to-r from-transparent via-[#c9a35a] to-transparent animate-pulse"
    />

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <GroupFolderModal
      v-if="showGroupFolderModal"
      v-model:show="showGroupFolderModal"
      :groups="groupListForFolders"
      :folders="groupFolders"
      :folder-map="groupFolderMap"
      @create-folder="createGroupFolder"
      @rename-folder="renameGroupFolder"
      @delete-folder="deleteGroupFolder"
      @assign="assignGroupToFolder"
    />

    <!-- Tabs en CELULAR (su propia fila): solo cuando isMobileChat; en escritorio
         van dentro del header (slot #tabs de ChatListHeader · P1 una sola fila). -->
    <Teleport defer to="#chatModuleTopbar" :disabled="isMobileChat">
    <PersonalChatTabs
      v-if="!hasAppliedFiltersOrActiveFolders && isPersonalBranchActive && isMobileChat"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      @chat-tab-change="updateAssigneeTab"
    />
    </Teleport>
    <GroupFolderBar
      v-if="!hasAppliedFiltersOrActiveFolders && isPersonalBranchActive && activeAssigneeTab === 'mygroups'"
      :folders="groupFolders"
      :active-id="activeGroupFolder"
      :counts="groupFolderCounts"
      :total-count="groupConversations.length"
      @select="selectGroupFolder"
      @manage="showGroupFolderModal = true"
    />
    <Teleport defer to="#chatModuleTopbar" :disabled="isMobileChat">
    <ChatTypeTabs
      v-if="!hasAppliedFiltersOrActiveFolders && !isPersonalBranchActive && isMobileChat"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      is-compact
      @chat-tab-change="updateAssigneeTab"
    />
    </Teleport>

    <!-- Sub-chips de sucursal para Impresión/Álbum (Victor 2026-06-11): la
         encargada filtra por una de sus 4 sucursales, con su color oficial. -->
    <div
      v-if="showDeptBranchChips"
      class="flex items-center gap-1.5 px-3 pb-2 overflow-x-auto"
    >
      <button
        class="flex-shrink-0 text-[11px] px-2.5 py-1 rounded-full border transition-colors"
        :class="!activeDeptBranch
          ? 'bg-n-alpha-2 text-n-slate-12 border-n-weak font-semibold'
          : 'text-n-slate-11 border-n-weak hover:text-n-slate-12'"
        @click="activeDeptBranch = null"
      >Todas</button>
      <button
        v-for="b in DEPT_BRANCH_CHIPS"
        :key="b.id"
        class="flex-shrink-0 flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border transition-colors"
        :class="activeDeptBranch === b.id ? 'font-semibold' : 'text-n-slate-11 hover:text-n-slate-12'"
        :style="activeDeptBranch === b.id
          ? { color: b.color, borderColor: b.color, backgroundColor: 'rgba(255,255,255,0.04)' }
          : { borderColor: 'rgba(255,255,255,0.10)' }"
        @click="activeDeptBranch = activeDeptBranch === b.id ? null : b.id"
      >
        <span class="size-[7px] rounded-full flex-shrink-0" :style="{ backgroundColor: b.color }" />
        {{ b.label }}
      </button>
    </div>


    <p
      v-if="isSearching"
      class="flex overflow-auto justify-center items-center p-4 text-n-slate-11"
    >
      Buscando…
    </p>
    <p
      v-else-if="activeAssigneeTab !== 'assigned_leads' && activeAssigneeTab !== 'tasks' && !chatListLoading && !conversationList.length"
      class="flex overflow-auto justify-center items-center p-4"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <ConversationBulkActions
      v-if="activeAssigneeTab !== 'assigned_leads' && selectedConversations.length"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      @select-all-conversations="toggleSelectAll"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />

    <!-- AK1 · Kanban Agentes inline cuando el tab es "Leads Asignados" -->
    <AgentsKanbanModal
      v-if="activeAssigneeTab === 'assigned_leads'"
      :inline="true"
      class="flex-1 min-h-0"
    />

    <div
      v-else
      ref="conversationListRef"
      class="flex-1 min-h-0 overflow-y-auto conversations-list"
      :class="{ '!overflow-hidden': isContextMenuOpen }"
    >
      <!-- Tab "Chats Urgentes" · 2026-05-27 (Victor): se renderiza IGUAL que Todos
           y Mi Chat (tarjeta normal ConversationItem). Sin cinta roja, sin agrupado
           por sucursal, sin botones Atender/Reclamar. La lista ya viene filtrada a
           las urgentes en conversationList (tab === 'urgent'). -->
      <MyTasksTimeline
        v-if="activeAssigneeTab === 'tasks'"
        :tasks="tasksList"
        :loading="tasksLoading"
        @changed="fetchTasks"
        @open-new="newTaskModalOpen = true"
      />

      <Virtualizer
        v-else
        :key="branchVirtualKey"
        ref="virtualListRef"
        v-slot="{ item, index }"
        :data="branchRenderRows"
      >
        <!-- Encabezado de sucursal (acordeón · P2 "punto + chip suave" · Victor
             2026-06-11): limpio y delicado, sin la barra dorada de fondo. Punto de
             color por sucursal + nombre en peso medio + conteo en píldora tenue.
             Altura FIJA (h-[50px] box-border) → el virtualizador tiene alturas
             deterministas y NUNCA monta una tarjeta sobre el encabezado. -->
        <button
          v-if="item.__branchHeader"
          class="flex items-center gap-2.5 w-full h-[50px] box-border px-4 border-b border-n-weak transition-colors hover:bg-n-alpha-1"
          @click="toggleChatBranch(item.key)"
        >
          <span
            class="inline-block text-[9px] transition-transform duration-200"
            :class="item.expanded ? 'rotate-90 text-n-amber-10' : 'text-n-slate-10'"
          >▸</span>
          <span v-if="item.isPersonal" class="text-[10px] shrink-0">🔒</span>
          <span class="size-2 rounded-full shrink-0" :style="{ background: branchDotColor(item.key) }"></span>
          <span class="text-[12.5px] font-semibold tracking-[0.2px] text-n-slate-12 truncate">{{ item.name }}</span>
          <span class="ml-auto text-[11px] font-bold text-n-slate-11 bg-n-alpha-1 border border-n-weak rounded-full px-2.5 py-0.5 tabular-nums">{{ branchCountsReady || !fullListLoading || isDepartmentTab ? item.count : '···' }}</span>
        </button>
        <ConversationItem
          v-else
          :source="item"
          :label="label"
          :team-id="teamId"
          :folders-id="foldersId"
          :conversation-type="conversationType"
          :show-assignee="showAssigneeInConversationCard"
          :data-index="index"
          @select-conversation="selectConversation"
          @de-select-conversation="deSelectConversation"
        />
      </Virtualizer>
      <div v-if="chatListLoading && !hasCurrentPageEndReached && !searchQuery.trim() && activeAssigneeTab !== 'tasks' && activeAssigneeTab !== 'urgent' && !isDepartmentTab" class="flex justify-center my-4">
        <Spinner class="text-n-brand" />
      </div>
      <p
        v-else-if="activeAssigneeTab !== 'urgent' && !isDepartmentTab && showEndOfListMessage"
        class="p-4 text-center text-n-slate-11"
      >
        {{ $t('CHAT_LIST.EOF') }}
      </p>
      <IntersectionObserver
        v-else-if="activeAssigneeTab !== 'urgent' && !isDepartmentTab && !searchQuery.trim()"
        :options="intersectionObserverOptions"
        @observed="loadMoreConversations"
      />
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="closeAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
    <NewTaskModal
      v-if="newTaskModalOpen"
      @close="newTaskModalOpen = false"
      @created="fetchTasks"
    />
  </div>
</template>

<style lang="scss">
/* ── OC-2 Mini Rail · refinado v2
   - 64px colapsado · solo muestra indicador (icono 💬 + label + counter)
   - 320px al hover · transición bouncy · muestra header/tabs/search/lista normales
   - Cuando no hay chat abierto, ChatList queda con su w-[340px] 2xl:w-[412px] normal
   - 0 cambios a lógica · solo CSS visual */
.conversations-list-wrap.oc2-rail {
  width: 64px;
  overflow: hidden;
  transition: width 0.32s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.32s;
  position: relative;
  z-index: 5;

  &:hover {
    width: 320px;
    box-shadow: 4px 0 30px rgba(0, 0, 0, 0.55);
  }

  // Cuando colapsado · ocultar TODOS los children con opacity (mantiene flex layout intacto)
  > *:not(.oc2-rail-avatars) {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s;
  }

  // Al hover · revelar children normalmente · ocultar el strip de avatares
  &:hover > *:not(.oc2-rail-avatars) {
    opacity: 1;
    pointer-events: auto;
  }
}

/* V4 · Avatar Strip · 2026-05-13 (Victor)
   Columna de avatares clickeables · visible cuando rail colapsado · oculta al hover */
.oc2-rail-avatars {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  z-index: 10;
  opacity: 1;
  transition: opacity 0.16s;
  pointer-events: auto;
}
.oc2-rail:hover .oc2-rail-avatars {
  opacity: 0;
  pointer-events: none;
}

.oc2-rail-avatars-head {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(251, 191, 36, 0.12);
}
.oc2-rail-head-icon {
  width: 22px;
  height: 22px;
  color: #fbbf24;
  filter: drop-shadow(0 0 6px rgba(251, 191, 36, 0.45));
}
.oc2-rail-head-count {
  position: absolute;
  top: 6px;
  right: 8px;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  background: #ef4444;
  border: 2px solid var(--color-n-surface-1, #050505);
  border-radius: 9px;
  font-size: 9px;
  font-weight: 800;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 6px rgba(239, 68, 68, 0.55);
}

.oc2-rail-avatars-list {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 0 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  scrollbar-width: thin;
  scrollbar-color: rgba(251, 191, 36, 0.25) transparent;
}
.oc2-rail-avatars-list::-webkit-scrollbar { width: 3px; }
.oc2-rail-avatars-list::-webkit-scrollbar-track { background: transparent; }
.oc2-rail-avatars-list::-webkit-scrollbar-thumb {
  background: rgba(251, 191, 36, 0.30);
  border-radius: 2px;
}

.oc2-rail-avatar {
  position: relative;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1a1209;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  border: 2px solid transparent;
  transition: transform 0.18s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.18s, border-color 0.18s;
  padding: 0;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
}
.oc2-rail-avatar:hover {
  transform: scale(1.10);
  box-shadow: 0 4px 14px rgba(251, 191, 36, 0.45), 0 0 0 2px rgba(251, 191, 36, 0.35);
}
.oc2-rail-avatar-active {
  border-color: #fbbf24;
  box-shadow: 0 0 0 2px rgba(251, 191, 36, 0.55), 0 4px 14px rgba(251, 191, 36, 0.40);
}
.oc2-rail-avatar-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  display: block;
}
.oc2-rail-avatar-initial {
  line-height: 1;
  user-select: none;
}

/* Channel badge bottom-right (iOS-style brand colors) */
.oc2-rail-avatar-ch {
  position: absolute;
  bottom: -2px;
  right: -3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid var(--color-n-surface-1, #050505);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
}
.oc2-rail-avatar-ch-svg {
  width: 9px;
  height: 9px;
}
.oc2-rail-avatar-ch-svg-tk {
  filter:
    drop-shadow(-0.5px 0 0 #25F4EE)
    drop-shadow(0.5px 0 0 #FE2C55);
}
.oc2-rail-avatar-ch-wa { background: linear-gradient(180deg, #4FCE5D, #29A71A); }
.oc2-rail-avatar-ch-ig {
  background: radial-gradient(circle at 30% 110%, #fdf497, #fd5949 45%, #d6249f 60%, #285aeb 90%);
}
.oc2-rail-avatar-ch-fb { background: #0084FF; }
.oc2-rail-avatar-ch-tk { background: #010101; }
/* Web/email/sms/etc: sin badge (no agregamos elemento .ch) */

/* Mini-dot unread top-right */
.oc2-rail-avatar-unread {
  position: absolute;
  top: -4px;
  right: -4px;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  background: #ef4444;
  color: #fff;
  font-size: 9px;
  font-weight: 800;
  border-radius: 9px;
  border: 2px solid var(--color-n-surface-1, #050505);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 6px rgba(239, 68, 68, 0.5);
}

/* ── Tab "Chats Urgentes" agrupado por agente (N · 2026-05-12 · M1.B)
   Cada agente es un acordeón colapsable. Estilo cristal negro + dorado.
   El estado expand/collapse se persiste en localStorage por agente. ── */
.urgent-groups {
  padding: 4px 0 12px;
}
/* V2 · 2026-05-14 (Victor) · header explicativo del tab Urgentes */
.ub-v2-header {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin: 8px 12px 14px;
  padding: 10px 14px;
  background: linear-gradient(135deg, rgba(244,63,94,0.10), rgba(40,30,18,0.55));
  border: 1px solid rgba(244,63,94,0.35);
  border-radius: 10px;
  font-size: 12px;
  color: #fda4af;
  font-weight: 700;
  box-shadow: 0 4px 14px rgba(244,63,94,0.10);
}
.ub-v2-header-title { color: #fda4af; font-weight: 800; }
.ub-v2-header-sub { color: rgba(255,255,255,0.55); font-weight: 500; font-size: 11px; }
.ub-v2-pill {
  margin-left: auto;
  background: rgba(40,180,90,0.18);
  border: 1px solid rgba(40,180,90,0.45);
  color: #6cd497;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.4px;
}
.ug-group {
  margin: 0 8px 10px;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(180deg, #0a0a0a 0%, #050505 100%);
  border: 1px solid rgba(251,191,36,0.18);
  box-shadow:
    inset 0 1px 0 rgba(251,191,36,0.06),
    0 4px 14px rgba(0,0,0,0.40);
}
.ug-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 11px 14px;
  background: linear-gradient(180deg, rgba(251,191,36,0.06), rgba(251,191,36,0.01));
  border: 0;
  border-bottom: 1px solid rgba(251,191,36,0.10);
  cursor: pointer;
  transition: background 0.18s;
  text-align: left;
  font-family: inherit;
}
.ug-header:hover {
  background: linear-gradient(180deg, rgba(251,191,36,0.12), rgba(251,191,36,0.03));
}
.ug-chevron {
  font-size: 10px;
  color: rgba(251,191,36,0.75);
  width: 12px;
  display: inline-block;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}
.ug-chevron-expanded { transform: rotate(90deg); }
.ug-avatar {
  width: 30px; height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  display: flex; align-items: center; justify-content: center;
  font-size: 11px;
  font-weight: 800;
  color: #1a1209;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(251,191,36,0.30);
}
.ug-avatar--unassigned {
  background: linear-gradient(135deg, #ef4444, #b91c1c);
  color: #fff;
  box-shadow: 0 2px 8px rgba(239,68,68,0.35);
}
.ug-name {
  flex: 1;
  font-size: 13.5px;
  font-weight: 800;
  color: #fff;
  letter-spacing: 0.2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ug-count {
  flex-shrink: 0;
  padding: 2px 9px;
  border-radius: 999px;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #1a1209;
  font-size: 11px;
  font-weight: 900;
  min-width: 24px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(251,191,36,0.30);
}
.ug-count--unassigned {
  background: linear-gradient(135deg, #ef4444, #b91c1c);
  color: #fff;
  box-shadow: 0 2px 6px rgba(239,68,68,0.35);
}
.ug-header--unassigned {
  background: linear-gradient(180deg, rgba(239,68,68,0.07), rgba(239,68,68,0.02));
  border-bottom-color: rgba(239,68,68,0.18);
}
.ug-header--unassigned:hover {
  background: linear-gradient(180deg, rgba(239,68,68,0.12), rgba(239,68,68,0.04));
}
.ug-body {
  background: rgba(0,0,0,0.20);
}

/* === P3 · Tab Urgentes jerárquico (Q1 · 2026-05-12) ===
   Sucursales como divider headers (sin cards anidadas) → Agentes con indent +
   avatar dorado → Tabla de conversaciones. Lo más neutral/minimalista. */
.ub-branch-header {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 16px 14px 12px;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  color: #fff;
  transition: background 0.18s;
}
.ub-branch-header:hover { background: rgba(255,255,255,0.015); }
.ub-chevron {
  font-size: 9px;
  color: rgba(255,255,255,0.45);
  width: 10px;
  display: inline-block;
  transition: transform 0.18s ease;
  flex-shrink: 0;
}
.ub-chevron-expanded { transform: rotate(90deg); }
.ub-branch-label {
  flex: 1;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 2px;
  color: rgba(255,255,255,0.72);
  text-transform: uppercase;
}
.ub-branch-label span {
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
  color: rgba(255,255,255,0.4);
  margin-left: 8px;
}
.ub-count {
  flex-shrink: 0;
  padding: 2px 9px;
  border-radius: 999px;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #1a1209;
  font-size: 11px;
  font-weight: 900;
  min-width: 24px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(251,191,36,0.25);
}
.ub-agents {
  padding: 4px 0 8px;
}
.ub-agent {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 14px 9px 32px;
  background: transparent;
  border: 0;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.15s;
}
.ub-agent:hover { background: rgba(255,255,255,0.03); }
.ub-agent-av {
  width: 26px; height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fbbf24, #d97706);
  display: flex; align-items: center; justify-content: center;
  font-size: 10px;
  font-weight: 800;
  color: #1a1209;
  flex-shrink: 0;
}
.ub-agent-av--unassigned {
  background: linear-gradient(135deg, #ef4444, #b91c1c);
  color: #fff;
}
.ub-agent-name {
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  color: rgba(255,255,255,0.88);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ub-agent-count {
  flex-shrink: 0;
  padding: 1px 8px;
  border-radius: 999px;
  background: rgba(251,191,36,0.18);
  color: #fbbf24;
  font-size: 10px;
  font-weight: 800;
  min-width: 22px;
  text-align: center;
  border: 1px solid rgba(251,191,36,0.25);
}
.ub-agent-count--unassigned {
  background: rgba(239,68,68,0.18);
  color: #fca5a5;
  border-color: rgba(239,68,68,0.30);
}
.ub-table-wrap {
  padding-left: 48px;
  padding-right: 14px;
  padding-bottom: 4px;
}
.ub-table-wrap .ug-table thead th { background: rgba(255,255,255,0.015); }

/* R3 · 2026-05-12 · Divider "EN PROCESO · SIENDO ATENDIDOS"
   Separa convs urgentes sin tomar (arriba) de las activamente atendidas (abajo).
   Línea punteada + texto uppercase tipo sección de sidebar Notion. */
.ub-section-divider {
  padding: 10px 14px 8px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 1.5px;
  color: rgba(255,255,255,0.4);
  text-transform: uppercase;
  border-top: 1px dashed rgba(255,255,255,0.10);
  margin-top: 6px;
}
.ub-table-wrap .ug-table--in-process tbody tr { background: rgba(255,255,255,0.005); }

/* Tabla del acordeón Urgentes (O3 · 2026-05-12) */
.ug-table {
  width: 100%;
  border-collapse: collapse;
}
.ug-table thead th {
  background: rgba(255,255,255,0.02);
  padding: 8px 14px;
  text-align: left;
  font-size: 10px;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  font-weight: 700;
  color: rgba(255,255,255,0.42);
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.ug-th-av    { width: 44px; }
.ug-th-time  { width: 130px; text-align: right; }
.ug-th-acts  { width: 110px; text-align: right; }
</style>
